<template>
  <div class="hvac-calculator" data-theme="v1">
    <div class="hvac-calculator__container">

      <!-- Page Header -->
      <div class="hvac-calculator__page-header">
        <div class="hvac-calculator__header-content">
          <div class="hvac-calculator__header-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div>
            <h2 class="hvac-calculator__page-title">Warehouse HVAC Calculator</h2>
            <p class="hvac-calculator__page-description">Estimate annual HVAC energy costs and peak loads for your warehouse</p>
          </div>
        </div>
        <a href="/calculator-toolbox" class="hvac-calculator__back-link">← Calculator Toolbox</a>
      </div>

      <!-- INPUTS (plain, no accordion) -->
      <div class="hvac-calculator__inputs-section">
          <!-- Mode Toggle -->
          <div class="hvac-calculator__mode-toggle">
            <button type="button" :class="['hvac-calculator__mode-btn', { active: mode === 'quick' }]" @click="mode = 'quick'">
              <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
              Quick Estimate
            </button>
            <button type="button" :class="['hvac-calculator__mode-btn', { active: mode === 'adv' }]" @click="mode = 'adv'">
              <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18"/></svg>
              Full Analysis
            </button>
          </div>

          <!-- QUICK FORM -->
          <div v-show="mode === 'quick'">
            <div class="hvac-calculator__form-section">
              <h3 class="hvac-calculator__section-title">A. Location &amp; Building</h3>
              <div class="hvac-calculator__grid">
                <div class="hvac-form-group">
                  <label>City / Region <span class="hvac-req">*</span></label>
                  <select v-model="quickCity" class="hvac-form-input">
                    <option v-for="(d, key) in CD" :key="key" :value="key">{{ d.n }}</option>
                  </select>
                  <span class="hvac-form-help" v-if="quickCity && CD[quickCity]">elec ${{ CD[quickCity].e.toFixed(2) }}/kWh · gas ${{ CD[quickCity].g.toFixed(2) }}/therm</span>
                </div>
                <div class="hvac-form-group">
                  <label>Floor Area (sq ft) <span class="hvac-req">*</span></label>
                  <input type="number" v-model.number="quickFloorArea" class="hvac-form-input" placeholder="e.g., 50000" />
                </div>
                <div class="hvac-form-group">
                  <label>Ceiling / Eave Height (ft) <span class="hvac-req">*</span></label>
                  <input type="number" v-model.number="quickCeilingHeight" class="hvac-form-input" placeholder="e.g., 24" />
                </div>
                <div class="hvac-form-group">
                  <label>Roof Age (years) <span class="hvac-req">*</span></label>
                  <input type="number" v-model.number="quickRoofAge" class="hvac-form-input" placeholder="e.g., 10" />
                </div>
              </div>
            </div>
            <div class="hvac-calculator__form-section">
              <h3 class="hvac-calculator__section-title">B. Operations &amp; Systems</h3>
              <div class="hvac-calculator__grid">
                <div class="hvac-form-group">
                  <label>Loading Dock Doors <span class="hvac-req">*</span></label>
                  <input type="number" v-model.number="quickDockDoors" class="hvac-form-input" placeholder="e.g., 4" />
                </div>
                <div class="hvac-form-group">
                  <label>Peak Workers on Shift <span class="hvac-req">*</span></label>
                  <input type="number" v-model.number="quickOccupancy" class="hvac-form-input" placeholder="e.g., 25" />
                </div>
                <div class="hvac-form-group">
                  <label>Cooling SEER Rating <span class="hvac-req">*</span>
                    <span class="hvac-tip-wrap">
                      <button type="button" class="hvac-tip-btn">?</button>
                      <span class="hvac-tip-text">Seasonal Energy Efficiency Ratio. Higher = more efficient. Federal commercial minimum is 14 SEER. Each point above 14 saves ~5–7% on annual cooling costs.</span>
                    </span>
                  </label>
                  <input type="number" v-model.number="quickSeer" step="0.5" class="hvac-form-input" placeholder="e.g., 16" />
                  <span class="hvac-form-help">Federal minimum: 14 SEER</span>
                </div>
                <div class="hvac-form-group">
                  <label>Heating Fuel</label>
                  <select v-model="quickFuel" class="hvac-form-input">
                    <option value="gas">Natural gas</option>
                    <option value="electric">All-electric</option>
                    <option value="propane">Propane / LP</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="hvac-calculator__actions">
              <button type="button" @click="doQuick" class="hvac-btn-calculate">
                <svg class="hvac-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                Calculate HVAC Loads
              </button>
              <button type="button" @click="resetForm" class="hvac-btn-secondary">Reset</button>
              <button type="button" @click="mode = 'adv'" class="hvac-btn-ghost">Full Analysis →</button>
            </div>
          </div>

          <!-- FULL ANALYSIS FORM -->
          <div v-show="mode === 'adv'">
            <div class="hvac-calculator__step-bar">
              <button v-for="(step, i) in steps" :key="i" type="button"
                :class="['hvac-calculator__step-btn', { active: currentStep === i, done: currentStep > i }]"
                @click="currentStep = i">
                {{ i + 1 }}. {{ step }}
              </button>
            </div>

            <!-- Step 0: Location -->
            <div v-show="currentStep === 0" class="hvac-calculator__form-section">
              <h3 class="hvac-calculator__section-title">A. Location &amp; Climate</h3>
              <div class="hvac-calculator__grid">
                <div class="hvac-form-group">
                  <label>City / Region <span class="hvac-req">*</span></label>
                  <select v-model="city" @change="updateCity" class="hvac-form-input">
                    <option v-for="(d, key) in CD" :key="key" :value="key">{{ d.n }} (Zone {{ d.z }})</option>
                  </select>
                  <span class="hvac-form-help" v-if="city && CD[city]">elec ${{ CD[city].e.toFixed(2) }}/kWh · gas ${{ CD[city].g.toFixed(2) }}/therm</span>
                </div>
                <div class="hvac-form-group">
                  <label>ASHRAE Climate Zone
                    <span class="hvac-tip-wrap">
                      <button type="button" class="hvac-tip-btn">?</button>
                      <span class="hvac-tip-text">ASHRAE climate zones (1A–8) define design temperature benchmarks. Hot-humid zones drive cooling; cold zones drive heating. Auto-filled from city.</span>
                    </span>
                  </label>
                  <input type="text" v-model="climateZone" class="hvac-form-input" readonly />
                </div>
                <div class="hvac-form-group">
                  <label>Cooling Design Temp (°F)</label>
                  <input type="number" v-model.number="coolingDesignTemp" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Heating Design Temp (°F)</label>
                  <input type="number" v-model.number="heatingDesignTemp" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Summer Humidity (grains/lb)</label>
                  <input type="number" v-model.number="humidity" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Indoor Cooling Setpoint (°F)</label>
                  <input type="number" v-model.number="indoorCoolingSetpoint" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Indoor Heating Setpoint (°F)</label>
                  <input type="number" v-model.number="indoorHeatingSetpoint" class="hvac-form-input" />
                </div>
              </div>
              <div class="hvac-calculator__step-nav">
                <span></span>
                <button type="button" @click="currentStep = 1" class="hvac-btn-primary">Next: Building →</button>
              </div>
            </div>

            <!-- Step 1: Building -->
            <div v-show="currentStep === 1" class="hvac-calculator__form-section">
              <h3 class="hvac-calculator__section-title">B. Building Envelope</h3>
              <div class="hvac-calculator__grid">
                <div class="hvac-form-group">
                  <label>Floor Area (sq ft) <span class="hvac-req">*</span></label>
                  <input type="number" v-model.number="floorArea" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Ceiling / Eave Height (ft) <span class="hvac-req">*</span></label>
                  <input type="number" v-model.number="ceilingHeight" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Building Shape</label>
                  <select v-model="buildingShape" class="hvac-form-input">
                    <option value="1.0">Square / compact</option>
                    <option value="1.15">Rectangular (wide)</option>
                    <option value="1.35">Rectangular (long/narrow)</option>
                    <option value="1.25">L-shaped</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Wall Construction</label>
                  <select v-model="wallType" @change="updateWallRValue" class="hvac-form-input">
                    <option value="13">Metal panel — R-13 batt</option>
                    <option value="19">Metal panel — R-19 batt</option>
                    <option value="25">Metal panel — R-25 batt</option>
                    <option value="2">8″ tilt-up concrete (bare)</option>
                    <option value="12">8″ tilt-up w/ insulation</option>
                    <option value="11">CMU block w/ R-11</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Wall R-Value
                    <span class="hvac-tip-wrap">
                      <button type="button" class="hvac-tip-btn">?</button>
                      <span class="hvac-tip-text">Thermal resistance — higher is better. Metal warehouse walls typically range R-13 to R-25. Auto-filled from wall type above.</span>
                    </span>
                  </label>
                  <input type="number" v-model.number="wallRValue" class="hvac-form-input" />
                  <span class="hvac-form-help">Auto-filled from wall type; can override</span>
                </div>
                <div class="hvac-form-group">
                  <label>Roof Type</label>
                  <select v-model="roofType" @change="updateRoofRValue" class="hvac-form-input">
                    <option value="20">Standing seam metal</option>
                    <option value="25">TPO / EPDM membrane</option>
                    <option value="15">Built-up (gravel surfaced)</option>
                    <option value="5">Concrete / precast</option>
                    <option value="6">Old corrugated metal</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Roof R-Value
                    <span class="hvac-tip-wrap">
                      <button type="button" class="hvac-tip-btn">?</button>
                      <span class="hvac-tip-text">Commercial warehouses typically range R-13 to R-38. Higher values pay back fastest — the roof has the largest exposed surface area.</span>
                    </span>
                  </label>
                  <input type="number" v-model.number="roofRValue" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Roof Age (years)</label>
                  <input type="number" v-model.number="roofAge" @input="updateRoofAgeDegradation" class="hvac-form-input" />
                  <span class="hvac-form-help">{{ roofDegradationNote }}</span>
                </div>
                <div class="hvac-form-group">
                  <label>Roof Reflectivity</label>
                  <select v-model="roofReflectivity" class="hvac-form-input">
                    <option value="0.85">Cool roof — white (ρ=0.85)</option>
                    <option value="0.65">Light gray (ρ=0.65)</option>
                    <option value="0.40">Medium gray (ρ=0.40)</option>
                    <option value="0.20">Dark / standard (ρ=0.20)</option>
                    <option value="0.10">Black membrane (ρ=0.10)</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Slab Insulation</label>
                  <select v-model="slabInsulation" class="hvac-form-input">
                    <option value="0">None — bare slab on grade</option>
                    <option value="5">R-5 perimeter only</option>
                    <option value="10">R-10 full slab</option>
                    <option value="15">R-15 full slab</option>
                  </select>
                </div>
              </div>
              <div class="hvac-calculator__step-nav">
                <button type="button" @click="currentStep = 0" class="hvac-btn-secondary">← Back</button>
                <button type="button" @click="currentStep = 2" class="hvac-btn-primary">Next: Openings →</button>
              </div>
            </div>

            <!-- Step 2: Openings -->
            <div v-show="currentStep === 2" class="hvac-calculator__form-section">
              <h3 class="hvac-calculator__section-title">C. Openings &amp; Infiltration</h3>
              <div class="hvac-calculator__grid">
                <div class="hvac-form-group">
                  <label>Loading Dock Doors <span class="hvac-req">*</span></label>
                  <input type="number" v-model.number="dockDoors" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Dock Seal Quality</label>
                  <select v-model="dockSeal" class="hvac-form-input">
                    <option value="0.90">Poor — no seals</option>
                    <option value="0.50">Standard dock seals</option>
                    <option value="0.25">Dock shelters + levelers</option>
                    <option value="0.10">Full dock enclosure</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Dock Doors Open (% of hours)</label>
                  <input type="number" v-model.number="dockDoorsOpenPct" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Personnel / Man Doors</label>
                  <input type="number" v-model.number="personnelDoors" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Door Cycles per Hour</label>
                  <input type="number" v-model.number="doorCyclesPerHour" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Skylights / Panels (sq ft)</label>
                  <input type="number" v-model.number="skylightArea" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Skylight Type</label>
                  <select v-model="skylightType" class="hvac-form-input">
                    <option value="0.70">Single-layer (SHGC 0.70)</option>
                    <option value="0.40">Double-layer (SHGC 0.40)</option>
                    <option value="0.25">Low-E coated (SHGC 0.25)</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Building Tightness</label>
                  <select v-model="buildingTightness" class="hvac-form-input">
                    <option value="0.10">Tight — new construction</option>
                    <option value="0.25">Average</option>
                    <option value="0.45">Leaky — older / poor sealing</option>
                  </select>
                </div>
              </div>
              <div class="hvac-calculator__step-nav">
                <button type="button" @click="currentStep = 1" class="hvac-btn-secondary">← Back</button>
                <button type="button" @click="currentStep = 3" class="hvac-btn-primary">Next: Systems →</button>
              </div>
            </div>

            <!-- Step 3: Systems -->
            <div v-show="currentStep === 3" class="hvac-calculator__form-section">
              <h3 class="hvac-calculator__section-title">D. HVAC Systems &amp; Efficiency</h3>
              <div class="hvac-calculator__grid">
                <div class="hvac-form-group">
                  <label>Cooling System Type</label>
                  <select v-model="coolingSystem" class="hvac-form-input">
                    <option value="rtu">Rooftop unit (RTU) — packaged</option>
                    <option value="split">Split DX system</option>
                    <option value="chiller">Chiller + AHU</option>
                    <option value="evap">Evaporative + DX hybrid</option>
                    <option value="none">No mechanical cooling</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Cooling SEER / EER
                    <span class="hvac-tip-wrap">
                      <button type="button" class="hvac-tip-btn">?</button>
                      <span class="hvac-tip-text">Federal commercial minimum is 14 SEER. High-efficiency units reach 18–20 SEER. Each point above 14 reduces annual cooling costs by ~5–7%.</span>
                    </span>
                  </label>
                  <input type="number" v-model.number="seer" step="0.5" class="hvac-form-input" />
                  <span class="hvac-form-help">Federal minimum: 14 SEER</span>
                </div>
                <div class="hvac-form-group">
                  <label>Heating System Type</label>
                  <select v-model="heatingSystem" class="hvac-form-input">
                    <option value="gas">Gas-fired unit heaters / RTU</option>
                    <option value="radiant">Gas infrared radiant tubes</option>
                    <option value="electric">Electric resistance</option>
                    <option value="hp">Heat pump</option>
                    <option value="hydronic">Hydronic — gas boiler</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Heating AFUE %
                    <span class="hvac-tip-wrap">
                      <button type="button" class="hvac-tip-btn">?</button>
                      <span class="hvac-tip-text">Annual Fuel Utilization Efficiency. Standard gas is 80% — 20% of fuel is wasted. High-efficiency condensing units reach 90–96% AFUE and often pay back in 3–5 years in cold climates.</span>
                    </span>
                  </label>
                  <input type="number" v-model.number="afue" class="hvac-form-input" />
                  <span class="hvac-form-help">80% standard; 90%+ high-efficiency</span>
                </div>
                <div class="hvac-form-group">
                  <label>Ventilation Strategy</label>
                  <select v-model="ventilation" class="hvac-form-input">
                    <option value="0.8">Minimal — code minimum</option>
                    <option value="1.0">Standard — ASHRAE 62.1</option>
                    <option value="1.3">Enhanced / demand-controlled</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>System Age (years)</label>
                  <input type="number" v-model.number="systemAge" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Primary Fuel Type</label>
                  <select v-model="fuelType" class="hvac-form-input">
                    <option value="gas">Natural gas</option>
                    <option value="propane">Propane / LP</option>
                    <option value="electric">All-electric</option>
                    <option value="oil">Fuel oil</option>
                  </select>
                </div>
              </div>
              <div class="hvac-calculator__step-nav">
                <button type="button" @click="currentStep = 2" class="hvac-btn-secondary">← Back</button>
                <button type="button" @click="currentStep = 4" class="hvac-btn-primary">Next: Internal Loads →</button>
              </div>
            </div>

            <!-- Step 4: Internal Loads -->
            <div v-show="currentStep === 4" class="hvac-calculator__form-section">
              <h3 class="hvac-calculator__section-title">E. Internal Loads &amp; Occupancy</h3>
              <div class="hvac-calculator__grid">
                <div class="hvac-form-group">
                  <label>Peak Occupancy (workers)</label>
                  <input type="number" v-model.number="occupancy" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Activity Level</label>
                  <select v-model="activityLevel" class="hvac-form-input">
                    <option value="250">Sedentary (office/supervisor)</option>
                    <option value="330">Light — walking, light lifting</option>
                    <option value="430">Moderate — picking, packing</option>
                    <option value="600">Heavy — manual labor</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Operating Hours / Day</label>
                  <input type="number" v-model.number="operatingHours" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Lighting Type</label>
                  <select v-model="lightingType" @change="updateLightingDensity" class="hvac-form-input">
                    <option value="0.8">LED high-bay</option>
                    <option value="1.4">T8 fluorescent</option>
                    <option value="2.0">Metal halide (HID)</option>
                    <option value="2.2">High-pressure sodium</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Lighting Density (W/sq ft)</label>
                  <input type="number" v-model.number="lightingDensity" step="0.1" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Forklifts / Powered Equipment</label>
                  <input type="number" v-model.number="forklifts" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Forklift Type</label>
                  <select v-model="forkliftType" class="hvac-form-input">
                    <option value="8000">Electric (battery)</option>
                    <option value="28000">Propane / LP</option>
                    <option value="0">None</option>
                  </select>
                </div>
                <div class="hvac-form-group">
                  <label>Process / Misc Heat (BTU/hr)</label>
                  <input type="number" v-model.number="processHeat" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Refrigerated Storage (sq ft)</label>
                  <input type="number" v-model.number="refrigeratedStorage" class="hvac-form-input" />
                </div>
                <div class="hvac-form-group">
                  <label>Warehouse Use Type</label>
                  <select v-model="warehouseUse" class="hvac-form-input">
                    <option value="1.0">General dry storage</option>
                    <option value="1.15">E-commerce / high-traffic</option>
                    <option value="0.85">Long-term bulk storage</option>
                    <option value="1.3">Food / beverage distribution</option>
                    <option value="1.2">Light manufacturing</option>
                  </select>
                </div>
              </div>
              <div class="hvac-calculator__step-nav">
                <button type="button" @click="currentStep = 3" class="hvac-btn-secondary">← Back</button>
                <button type="button" @click="runCalc" class="hvac-btn-calculate">
                  <svg class="hvac-btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
                  Calculate HVAC Loads
                </button>
                <button type="button" @click="resetForm" class="hvac-btn-secondary">Reset</button>
              </div>
            </div>
          </div><!-- end adv form -->
      </div><!-- end inputs section -->

      <!-- ACCORDION: RESULTS -->
      <div class="hvac-accordion hvac-accordion--results" v-if="hasResults">
        <div class="hvac-accordion__header" @click="resultsOpen = !resultsOpen">
          <div class="hvac-accordion__header-left">
            <svg class="hvac-accordion__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
            <span>Results — {{ resultCity }}</span>
          </div>
          <svg :class="['hvac-accordion__chevron', { open: resultsOpen }]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </div>

        <transition name="hvac-slide">
        <div v-show="resultsOpen" class="hvac-accordion__body">
          <!-- KPI Tiles -->
          <div class="hvac-calculator__kpi-grid">
            <div class="hvac-kpi-card hvac-kpi-card--cooling">
              <div class="hvac-kpi-card__label">Peak cooling load</div>
              <div class="hvac-kpi-card__value">{{ resultCoolingTons !== null ? resultCoolingTons.toFixed(1) : '—' }}</div>
              <div class="hvac-kpi-card__unit">tons of refrigeration</div>
            </div>
            <div class="hvac-kpi-card hvac-kpi-card--heating">
              <div class="hvac-kpi-card__label">Peak heating load</div>
              <div class="hvac-kpi-card__value">{{ resultHeatingMBH !== null ? resultHeatingMBH.toFixed(0) : '—' }}</div>
              <div class="hvac-kpi-card__unit">MBH (1,000 BTU/hr)</div>
            </div>
            <div class="hvac-kpi-card hvac-kpi-card--cooling">
              <div class="hvac-kpi-card__label">Cooling units needed</div>
              <div class="hvac-kpi-card__value">{{ resultUnitsNeeded !== null ? resultUnitsNeeded : '—' }}</div>
              <div class="hvac-kpi-card__unit">× 20-ton RTUs (suggested)</div>
            </div>
            <div class="hvac-kpi-card">
              <div class="hvac-kpi-card__label">Effective roof R-value</div>
              <div class="hvac-kpi-card__value">{{ resultRoofRValue !== null ? 'R-' + resultRoofRValue.toFixed(0) : '—' }}</div>
              <div class="hvac-kpi-card__unit">after age degradation</div>
            </div>
          </div>

          <!-- Cost Banner -->
          <div class="hvac-calculator__cost-banner">
            <div class="hvac-cost-banner__item hvac-cost-banner__item--total">
              <div class="hvac-cost-banner__label">Estimated total annual energy cost</div>
              <div class="hvac-cost-banner__value">${{ annualTotal !== null ? fmtCurrency(annualTotal) : '—' }} / yr</div>
              <div class="hvac-cost-banner__sub">{{ ratesLabel }}</div>
            </div>
            <div class="hvac-cost-banner__item">
              <div class="hvac-cost-banner__item-lbl">Cooling (electricity)</div>
              <div class="hvac-cost-banner__item-val hvac-cost-banner__item-val--cool">${{ annualCooling !== null ? fmtCurrency(annualCooling) : '—' }}/yr</div>
            </div>
            <div class="hvac-cost-banner__item">
              <div class="hvac-cost-banner__item-lbl">Heating</div>
              <div class="hvac-cost-banner__item-val hvac-cost-banner__item-val--heat">${{ annualHeating !== null ? fmtCurrency(annualHeating) : '—' }}/yr</div>
            </div>
          </div>

          <!-- Two-column layout for health + upgrades + breakdown -->
          <div class="hvac-results-columns">

            <!-- Left column: Health checks -->
            <div class="hvac-results-col">
              <div class="hvac-calculator__form-section">
                <h3 class="hvac-calculator__section-title">System Health Check</h3>
                <div class="hvac-health-list">
                  <div v-for="(check, i) in healthChecks" :key="i" :class="['hvac-health-item', 'hvac-health-item--' + check.type]">
                    <div class="hvac-health-dot"></div>
                    <div>
                      <div class="hvac-health-title">{{ check.title }}</div>
                      <div class="hvac-health-body">{{ check.body }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right column: Upgrades -->
            <div class="hvac-results-col">
              <div class="hvac-calculator__form-section">
                <h3 class="hvac-calculator__section-title">What If I Upgrade?</h3>
                <div class="hvac-upgrade-list">
                  <div class="hvac-upgrade-row">
                    <label class="hvac-upgrade-label"><input type="checkbox" v-model="u1" @change="updateUpgrades" /> Upgrade cooling to SEER 18</label>
                    <div class="hvac-upgrade-right">
                      <div :class="['hvac-upgrade-save', { 'hvac-upgrade-save--off': upgradeSavings.s1 === 0 }]">{{ upgradeSavings.s1 > 0 ? '$' + fmtCurrency(upgradeSavings.s1) + ' / yr' : 'Already optimal' }}</div>
                      <div class="hvac-upgrade-note">{{ upgradeNotes.u1 }}</div>
                    </div>
                  </div>
                  <div class="hvac-upgrade-row">
                    <label class="hvac-upgrade-label"><input type="checkbox" v-model="u2" @change="updateUpgrades" /> Improve roof insulation to R-38</label>
                    <div class="hvac-upgrade-right">
                      <div :class="['hvac-upgrade-save', { 'hvac-upgrade-save--off': upgradeSavings.s2 === 0 }]">{{ upgradeSavings.s2 > 0 ? '$' + fmtCurrency(upgradeSavings.s2) + ' / yr' : 'Already optimal' }}</div>
                      <div class="hvac-upgrade-note">{{ upgradeNotes.u2 }}</div>
                    </div>
                  </div>
                  <div class="hvac-upgrade-row">
                    <label class="hvac-upgrade-label"><input type="checkbox" v-model="u3" @change="updateUpgrades" /> Install dock shelters on all doors</label>
                    <div class="hvac-upgrade-right">
                      <div :class="['hvac-upgrade-save', { 'hvac-upgrade-save--off': upgradeSavings.s3 === 0 }]">{{ upgradeSavings.s3 > 0 ? '$' + fmtCurrency(upgradeSavings.s3) + ' / yr' : 'Already optimal' }}</div>
                      <div class="hvac-upgrade-note">{{ upgradeNotes.u3 }}</div>
                    </div>
                  </div>
                  <div class="hvac-upgrade-row">
                    <label class="hvac-upgrade-label"><input type="checkbox" v-model="u4" @change="updateUpgrades" /> Upgrade to 90% AFUE heating</label>
                    <div class="hvac-upgrade-right">
                      <div :class="['hvac-upgrade-save', { 'hvac-upgrade-save--off': upgradeSavings.s4 === 0 }]">{{ upgradeSavings.s4 > 0 ? '$' + fmtCurrency(upgradeSavings.s4) + ' / yr' : 'Already optimal' }}</div>
                      <div class="hvac-upgrade-note">{{ upgradeNotes.u4 }}</div>
                    </div>
                  </div>
                  <div class="hvac-upgrade-total">
                    <span class="hvac-upgrade-total__label">Total potential annual savings</span>
                    <span class="hvac-upgrade-total__val">${{ fmtCurrency(totalUpgradeSavings) }} / yr</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Load Breakdown -->
          <div class="hvac-calculator__form-section">
            <h3 class="hvac-calculator__section-title">Load Breakdown</h3>
            <div class="hvac-calculator__breakdown-tables">
              <div class="hvac-breakdown-table">
                <div class="hvac-breakdown-table__title">Cooling</div>
                <themed-table :data="coolingBreakdown" :columns="breakdownColumns" :hoverable="false" size="sm" />
              </div>
              <div class="hvac-breakdown-table">
                <div class="hvac-breakdown-table__title">Heating</div>
                <themed-table :data="heatingBreakdown" :columns="breakdownColumns" :hoverable="false" size="sm" />
              </div>
            </div>
          </div>

          <!-- Print (hidden — available if client requests) -->
          <!-- <div class="hvac-calculator__actions">
            <button type="button" @click="printPage" class="hvac-btn-primary">Print / Save PDF</button>
          </div> -->

        </div><!-- end results accordion body -->
        </transition>
      </div><!-- end results accordion -->

    </div>
  </div>
</template>

<script>
const CD = {
  miami:       { z: '1A', c: 91,  h: 45,  u: 178, e: 0.13, g: 1.20, n: 'Miami, FL' },
  houston:     { z: '2A', c: 96,  h: 28,  u: 157, e: 0.10, g: 0.85, n: 'Houston, TX' },
  phoenix:     { z: '2B', c: 109, h: 31,  u: 50,  e: 0.12, g: 1.15, n: 'Phoenix, AZ' },
  losangeles:  { z: '3B', c: 88,  h: 37,  u: 48,  e: 0.22, g: 1.40, n: 'Los Angeles, CA' },
  atlanta:     { z: '3A', c: 94,  h: 18,  u: 131, e: 0.11, g: 0.90, n: 'Atlanta, GA' },
  dallas:      { z: '3A', c: 100, h: 22,  u: 122, e: 0.11, g: 0.80, n: 'Dallas, TX' },
  lasvegas:    { z: '3B', c: 108, h: 28,  u: 35,  e: 0.11, g: 1.00, n: 'Las Vegas, NV' },
  charlotte:   { z: '4A', c: 93,  h: 18,  u: 120, e: 0.11, g: 0.95, n: 'Charlotte, NC' },
  newyork:     { z: '4A', c: 90,  h: 12,  u: 112, e: 0.19, g: 1.85, n: 'New York, NY' },
  seattle:     { z: '4C', c: 83,  h: 23,  u: 62,  e: 0.10, g: 1.05, n: 'Seattle, WA' },
  portland:    { z: '4C', c: 89,  h: 21,  u: 67,  e: 0.10, g: 1.10, n: 'Portland, OR' },
  albuquerque: { z: '4B', c: 96,  h: 14,  u: 40,  e: 0.12, g: 0.75, n: 'Albuquerque, NM' },
  chicago:     { z: '5A', c: 89,  h: -4,  u: 95,  e: 0.13, g: 0.85, n: 'Chicago, IL' },
  denver:      { z: '5B', c: 93,  h: 1,   u: 60,  e: 0.12, g: 0.80, n: 'Denver, CO' },
  boston:      { z: '5A', c: 91,  h: 6,   u: 107, e: 0.22, g: 1.60, n: 'Boston, MA' },
  detroit:     { z: '5A', c: 88,  h: 1,   u: 105, e: 0.16, g: 0.95, n: 'Detroit, MI' },
  saltlake:    { z: '5B', c: 97,  h: 8,   u: 48,  e: 0.10, g: 0.80, n: 'Salt Lake City, UT' },
  minneapolis: { z: '6A', c: 88,  h: -16, u: 94,  e: 0.13, g: 0.80, n: 'Minneapolis, MN' },
};

const DEFAULT_DATA = () => ({
  CD,
  mode: 'quick', currentStep: 0,
  inputsOpen: true, resultsOpen: false, hasResults: false,
  activeTip: null,
  steps: ['Location', 'Building', 'Openings', 'Systems', 'Loads'],
  quickCity: 'chicago', quickFloorArea: 50000, quickCeilingHeight: 24,
  quickRoofAge: 10, quickDockDoors: 4, quickOccupancy: 25, quickSeer: 16, quickFuel: 'gas',
  city: 'chicago', climateZone: '5A', coolingDesignTemp: 89, heatingDesignTemp: -4,
  humidity: 95, indoorCoolingSetpoint: 75, indoorHeatingSetpoint: 65,
  floorArea: 50000, ceilingHeight: 24, buildingShape: '1.15',
  wallType: '13', wallRValue: 13, roofType: '25', roofRValue: 25,
  roofAge: 10, roofReflectivity: '0.40', slabInsulation: '10', roofDegradationNote: 'Degradation: ~6%',
  dockDoors: 4, dockSeal: '0.50', dockDoorsOpenPct: 20, personnelDoors: 6,
  doorCyclesPerHour: 10, skylightArea: 500, skylightType: '0.40', buildingTightness: '0.25',
  coolingSystem: 'rtu', seer: 16, heatingSystem: 'gas', afue: 80,
  ventilation: '1.0', systemAge: 5, fuelType: 'gas',
  occupancy: 25, activityLevel: '330', operatingHours: 10, lightingType: '0.8',
  lightingDensity: 0.8, forklifts: 3, forkliftType: '8000',
  processHeat: 0, refrigeratedStorage: 0, warehouseUse: '1.0',
  resultCity: '', resultCoolingTons: null, resultHeatingMBH: null,
  resultUnitsNeeded: null, resultRoofRValue: null,
  annualTotal: null, annualCooling: null, annualHeating: null, ratesLabel: '',
  healthChecks: [], coolingBreakdown: [], heatingBreakdown: [],
  u1: false, u2: false, u3: false, u4: false,
  upgradeSavings: { s1: 0, s2: 0, s3: 0, s4: 0 },
  upgradeNotes: { u1: '', u2: '', u3: '', u4: '' },
  totalUpgradeSavings: 0,
});

export default {
  name: 'HvacCalculator',

  data() { return DEFAULT_DATA(); },

  computed: {
    breakdownColumns() {
      return [
        { field: 'label', label: 'Component', sortable: false },
        { field: 'value', label: 'BTU/hr', sortable: false, formatter: (v) => new Intl.NumberFormat('en-US').format(v || 0) },
        { field: 'pct', label: '%', sortable: false, formatter: (v) => v !== null && v !== undefined ? v + '%' : '—' },
      ];
    },
  },

  methods: {
    updateCity() {
      const d = CD[this.city]; if (!d) return;
      this.climateZone = d.z; this.coolingDesignTemp = d.c;
      this.heatingDesignTemp = d.h; this.humidity = d.u;
    },
    updateWallRValue() { this.wallRValue = parseFloat(this.wallType); },
    updateRoofRValue() { this.roofRValue = parseFloat(this.roofType); },
    updateLightingDensity() { this.lightingDensity = parseFloat(this.lightingType); },
    updateRoofAgeDegradation() {
      const a = parseFloat(this.roofAge) || 0;
      const p = a > 20 ? 25 : a > 15 ? 18 : a > 10 ? 12 : a > 5 ? 6 : 0;
      this.roofDegradationNote = 'Degradation: ~' + p + '%';
      return p / 100;
    },
    doQuick() {
      const qd = CD[this.quickCity] || CD.chicago;
      this.city = this.quickCity; this.climateZone = qd.z;
      this.coolingDesignTemp = qd.c; this.heatingDesignTemp = qd.h; this.humidity = qd.u;
      this.floorArea = this.quickFloorArea || 50000;
      this.ceilingHeight = this.quickCeilingHeight || 24;
      this.roofAge = this.quickRoofAge || 10;
      this.dockDoors = this.quickDockDoors || 4;
      this.occupancy = this.quickOccupancy || 25;
      this.seer = this.quickSeer || 16;
      this.fuelType = this.quickFuel;
      this.updateRoofAgeDegradation();
      this.calc();
    },
    runCalc() { this.calc(); },
    calc() {
      const cd = CD[this.city] || CD.chicago;
      const eR = cd.e, gR = cd.g;
      const fa = parseFloat(this.floorArea) || 0;
      const ch = parseFloat(this.ceilingHeight) || 0;
      const sf = parseFloat(this.buildingShape) || 1.15;
      const wallA = Math.sqrt(fa) * sf * ch * 4;
      const tco = parseFloat(this.coolingDesignTemp) || 89;
      const tho = parseFloat(this.heatingDesignTemp) || -4;
      const tci = parseFloat(this.indoorCoolingSetpoint) || 75;
      const thi = parseFloat(this.indoorHeatingSetpoint) || 65;
      const dtC = tco - tci, dtH = thi - tho;
      const rdeg = this.updateRoofAgeDegradation();
      const rR = (parseFloat(this.roofRValue) || 25) * (1 - rdeg);
      const wR = parseFloat(this.wallRValue) || 13;
      const uR = 1 / Math.max(rR, 1), uW = 1 / Math.max(wR, 1);
      const absorb = 1 - parseFloat(this.roofReflectivity || '0.40');
      const dd = parseFloat(this.dockDoors) || 0;
      const md = parseFloat(this.personnelDoors) || 6;
      const ska = parseFloat(this.skylightArea) || 500;
      const ddA = dd * 80;
      const netW = Math.max(wallA - ddA - (md * 21) - ska, 0);
      const roofCC = fa * uR * (dtC + absorb * 22);
      const roofHC = fa * uR * dtH;
      const wallCC = netW * uW * dtC * 0.65;
      const wallHC = netW * uW * dtH;
      const dsf = parseFloat(this.dockSeal || '0.50');
      const dopn = parseFloat(this.dockDoorsOpenPct) || 20;
      const dockCFM = (ddA * dsf * 0.5) + (dd * (dopn / 100) * 80 * 25);
      const manCFM = md * ((parseFloat(this.doorCyclesPerHour) || 10) * 2.1);
      const genCFM = fa * ch * parseFloat(this.buildingTightness || '0.25') / 60;
      const totCFM = dockCFM + manCFM + genCFM;
      const hum = parseFloat(this.humidity) || 95;
      const inCS = totCFM * 1.1 * dtC, inCL = totCFM * 0.68 * Math.max(hum - 50, 0), inH = totCFM * 1.1 * dtH;
      const shgc = parseFloat(this.skylightType || '0.40');
      const skyC = ska * shgc * 150 + ska * 2 * dtC * 0.5, skyH = ska * 2 * dtH;
      const occ = parseFloat(this.occupancy) || 25;
      const actB = parseFloat(this.activityLevel) || 330;
      const occC = occ * actB * 0.65, occL = occ * actB * 0.35;
      const lB = fa * (parseFloat(this.lightingDensity) || 0.8) * 3.41;
      const fkB = (parseFloat(this.forklifts) || 0) * (parseFloat(this.forkliftType) || 8000);
      const phB = parseFloat(this.processHeat) || 0;
      const refB = (parseFloat(this.refrigeratedStorage) || 0) * 12;
      const uf = parseFloat(this.warehouseUse || '1.0');
      const vcfm = (occ || 25) * 17 * parseFloat(this.ventilation || '1.0');
      const ventC = vcfm * 1.1 * dtC + vcfm * 0.68 * Math.max(hum - 50, 0), ventH = vcfm * 1.1 * dtH;
      const totC = (roofCC + wallCC + skyC + inCS + inCL + ventC + occC + occL + lB + fkB + phB + refB) * uf;
      const totH = Math.max((roofHC + wallHC + skyH + inH + ventH - (occC + lB * 0.5)) * uf, 0);
      const sC = totC * 1.10, sH = totH * 1.15;
      const tons = sC / 12000, mbh = sH / 1000, units = Math.ceil(tons / 20);
      const seer = parseFloat(this.seer) || 16;
      const sysAdj = Math.min((parseFloat(this.systemAge) || 5) * 0.007, 0.15);
      const effSeer = seer * (1 - sysAdj);
      const oh = parseFloat(this.operatingHours) || 10;
      const coolkWh = sC * (oh * 90) / (effSeer * 1000);
      const afue = (parseFloat(this.afue) || 80) / 100;
      const isE = this.fuelType === 'electric';
      const hTherms = sH * (oh * 120) / (afue * 100000);
      const accC = coolkWh * eR;
      const ahC = isE ? (sH * (oh * 120) / 3412) * eR : hTherms * gR;
      const tot = accC + ahC;
      this.resultCity = cd.n;
      this.resultCoolingTons = tons; this.resultHeatingMBH = mbh; this.resultUnitsNeeded = units; this.resultRoofRValue = rR;
      this.annualCooling = Math.round(accC); this.annualHeating = Math.round(ahC); this.annualTotal = Math.round(tot);
      this.ratesLabel = 'at $' + eR.toFixed(2) + '/kWh · $' + gR.toFixed(2) + '/therm (' + cd.n + ')';
      const cCentries = [
        ['Roof conduction', roofCC], ['Wall conduction', wallCC], ['Skylights', skyC],
        ['Infiltration (sensible)', inCS], ['Infiltration (latent)', inCL],
        ['Ventilation', ventC], ['Occupants', occC + occL], ['Lighting', lB], ['Equipment & forklifts', fkB + phB + refB],
      ];
      const cHentries = [
        ['Roof conduction', roofHC], ['Wall conduction', wallHC], ['Skylights / glass', skyH],
        ['Infiltration', inH], ['Ventilation', ventH], ['Internal gain credit', -(occC + lB * 0.5)],
      ];
      this.coolingBreakdown = cCentries.map(([label, value]) => ({ label, value: Math.round(value), pct: totC > 0 ? Math.abs(Math.round(value / totC * 100)) : 0 }));
      this.coolingBreakdown.push({ label: 'Total (w/ safety factor)', value: Math.round(sC), pct: null });
      this.heatingBreakdown = cHentries.map(([label, value]) => ({ label, value: Math.round(value), pct: totH > 0 ? Math.abs(Math.round(value / totH * 100)) : 0 }));
      this.heatingBreakdown.push({ label: 'Total (w/ safety factor)', value: Math.round(sH), pct: null });
      // Health checks
      const checks = [];
      const seerN = seer, afueN = parseFloat(this.afue) || 80;
      const rageN = parseFloat(this.roofAge) || 10, sysaN = parseFloat(this.systemAge) || 5;
      const dsfN = dsf, ddN = dd;
      if (seerN < 14) checks.push({ type: 'crit', title: 'SEER below federal minimum', body: 'Rated SEER ' + seerN + ' is below the 14 SEER commercial minimum. Equipment likely needs replacement.' });
      else if (seerN < 16) checks.push({ type: 'warn', title: 'SEER ' + seerN + ' — below recommended', body: 'Upgrading to SEER 18 could save approximately $' + Math.round(accC * (1 - effSeer / 18)).toLocaleString() + '/yr on cooling.' });
      else checks.push({ type: 'ok', title: 'Cooling efficiency — SEER ' + seerN, body: 'Above the federal minimum. High-efficiency options (SEER 18–20) available for further savings.' });
      if (rageN > 20) checks.push({ type: 'crit', title: 'Roof age: ' + rageN + ' years — significant degradation', body: 'Insulation may have lost 25%+ of its rated R-value. Replacement or a cover board is recommended.' });
      else if (rageN > 12) checks.push({ type: 'warn', title: 'Roof age: ' + rageN + ' years — minor degradation', body: 'Some insulation degradation expected. Budget for replacement in the next 5 years.' });
      else checks.push({ type: 'ok', title: 'Roof age: ' + rageN + ' years', body: 'Within expected service life with minimal degradation.' });
      if (sysaN > 15) checks.push({ type: 'crit', title: 'HVAC system age: ' + sysaN + ' years', body: 'Well past typical service life. Real-world efficiency is significantly below nameplate rating.' });
      else if (sysaN > 10) checks.push({ type: 'warn', title: 'System age: ' + sysaN + ' years', body: 'Efficiency has likely declined ~' + Math.round(sysaN * 0.7) + '% from rated. Evaluate replacement ROI.' });
      else checks.push({ type: 'ok', title: 'System age: ' + sysaN + ' years', body: 'Within normal service life with minor efficiency degradation.' });
      if (ddN > 3 && dsfN > 0.4) checks.push({ type: 'warn', title: 'Dock infiltration risk — ' + ddN + ' doors, minimal sealing', body: 'Standard seals on ' + ddN + ' dock doors contribute significantly to infiltration load. Dock shelters offer fast payback.' });
      else checks.push({ type: 'ok', title: 'Dock infiltration control', body: 'Dock seal quality appears adequate for the number of doors.' });
      if (!isE && afueN < 80) checks.push({ type: 'crit', title: 'Heating AFUE ' + afueN + '% — below code minimum', body: 'AFUE below 80% does not meet current energy codes. Replacement required.' });
      else if (!isE && afueN < 90) checks.push({ type: 'warn', title: 'Heating efficiency — AFUE ' + afueN + '%', body: 'Upgrading to 90%+ condensing equipment could save $' + Math.round(ahC * (1 - afue / 0.92)).toLocaleString() + '/yr on heating.' });
      else if (!isE) checks.push({ type: 'ok', title: 'Heating efficiency — AFUE ' + afueN + '%', body: 'High-efficiency heating. Savings from further upgrades would be modest.' });
      this.healthChecks = checks;
      // Upgrade savings
      const rp = roofCC / Math.max(totC, 1), rph = roofHC / Math.max(totH, 1);
      const dp = dockCFM * 1.1 * dtC / Math.max(totC, 1), dph = dockCFM * 1.1 * dtH / Math.max(totH, 1);
      const s1 = effSeer < 18 ? accC * (1 - effSeer / 18) : 0;
      const s2 = rR < 38 ? ((1 / rR - 1 / 38) / (1 / rR)) * (accC * rp + ahC * rph) * 0.9 : 0;
      const s3 = dsfN > 0.25 && ddN > 0 ? ((dsfN - 0.25) / dsfN) * (accC * dp * 2.5 + ahC * dph * 2.5) : 0;
      const s4 = !isE && afueN < 90 ? ahC * (1 - afue / 0.92) : 0;
      this.upgradeSavings = { s1: Math.max(Math.round(s1), 0), s2: Math.max(Math.round(s2), 0), s3: Math.max(Math.round(s3), 0), s4: Math.max(Math.round(s4), 0) };
      this.upgradeNotes = {
        u1: s1 > 0 ? 'SEER ' + seerN + ' → 18' : '',
        u2: s2 > 0 ? 'R-' + Math.round(rR) + ' → R-38' : '',
        u3: s3 > 0 ? 'Seal factor ' + dsfN + ' → dock shelters' : '',
        u4: s4 > 0 ? afueN + '% AFUE → 92% condensing' : '',
      };
      this.u1 = false; this.u2 = false; this.u3 = false; this.u4 = false;
      this.updateUpgrades();
      // Open results, optionally collapse inputs
      this.hasResults = true;
      this.resultsOpen = true;
    },
    updateUpgrades() {
      let t = 0;
      if (this.u1) t += this.upgradeSavings.s1;
      if (this.u2) t += this.upgradeSavings.s2;
      if (this.u3) t += this.upgradeSavings.s3;
      if (this.u4) t += this.upgradeSavings.s4;
      this.totalUpgradeSavings = t;
    },
    resetForm() {
      const fresh = DEFAULT_DATA();
      Object.keys(fresh).forEach(k => { this[k] = fresh[k]; });
    },
    printPage() { window.print(); },
    fmtCurrency(val) { return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0); },
    fmtNum(val) { return new Intl.NumberFormat('en-US').format(val || 0); },
  },
};
</script>
